import React from 'react';

// Read-only guard for sold cars. A disabled <fieldset> also killed view buttons
// (open a signed doc, download, print), so this blocks events instead:
//   - clicks and presses on controls are cancelled,
//   - text fields turn readOnly on focus, so text can still be selected and copied,
//   - file drops are cancelled,
//   - anything inside [data-sold-ok] works as normal; [data-sold-drop-ok] takes drops only.
// Tabs, links and accordion headers only change the view, so they always work.
//
// Events from portals (dialogs, menus) reach these React handlers but sit outside
// this DOM node, so they pass. A Select or Autocomplete can't open its menu
// because its trigger is blocked here. To guard a dialog's content, wrap it in
// another <SoldLock>.

export const SoldLockContext = React.createContext(false);
export const useSoldLock = () => React.useContext(SoldLockContext);

const ALLOW = '[data-sold-ok], [role="tab"], a[href], .MuiAccordionSummary-root';
const CONTROL = [
  'button', 'input', 'select', 'textarea', 'label',
  '[role="button"]', '[role="combobox"]', '[role="checkbox"]', '[role="switch"]',
  '[role="radio"]', '[role="option"]', '[role="menuitem"]', '[role="slider"]',
  '.MuiChip-clickable',
].join(', ');
const TEXT_TYPES = ['text', 'number', 'email', 'tel', 'url', 'search', 'date', 'datetime-local', 'month', 'time', 'week', ''];
const READ_KEYS = ['Tab', 'Escape', 'Shift', 'Control', 'Meta', 'Alt', 'Home', 'End', 'ArrowLeft', 'ArrowRight'];

const DROP_OK = '[data-sold-ok], [data-sold-drop-ok]';

const isText = el => el.tagName === 'TEXTAREA'
  || (el.tagName === 'INPUT' && TEXT_TYPES.includes(el.type) && !el.closest('.MuiAutocomplete-root'));

function lockedControl(e) {
  const el = e.target.closest && e.target.closest(CONTROL);
  if (!el || !e.currentTarget.contains(el) || el.closest(ALLOW)) return null;
  return el;
}

export default function SoldLock({ locked = true, children }) {
  const ref = React.useRef(null);

  // Give back the fields this guard set readOnly once the car unlocks.
  React.useEffect(() => {
    if (locked || !ref.current) return;
    ref.current.querySelectorAll('[data-sold-readonly]').forEach(el => {
      el.readOnly = false;
      delete el.dataset.soldReadonly;
    });
  }, [locked]);

  if (!locked) return <div ref={ref}>{children}</div>;

  const blockPress = e => {
    const el = lockedControl(e);
    if (!el || isText(el)) return;
    e.preventDefault();
    e.stopPropagation();
  };

  const blockKeys = e => {
    const el = lockedControl(e);
    if (!el || READ_KEYS.includes(e.key)) return;
    if (isText(el) && (e.metaKey || e.ctrlKey) && ['a', 'c'].includes(e.key.toLowerCase())) return;
    e.preventDefault();
    e.stopPropagation();
  };

  const blockDrop = e => {
    if (!e.currentTarget.contains(e.target) || (e.target.closest && e.target.closest(DROP_OK))) return;
    // preventDefault also stops the browser from opening the dropped file.
    e.preventDefault();
    e.stopPropagation();
  };

  const makeReadOnly = e => {
    const el = lockedControl(e);
    if (el && isText(el) && !el.readOnly) {
      el.readOnly = true;
      el.dataset.soldReadonly = 'true';
    }
  };

  return (
    <div
      ref={ref}
      onClickCapture={blockPress}
      onMouseDownCapture={blockPress}
      onPointerDownCapture={blockPress}
      onKeyDownCapture={blockKeys}
      onFocusCapture={makeReadOnly}
      onDropCapture={blockDrop}
    >
      {children}
    </div>
  );
}
